import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { GoogleAuthProvider, onAuthStateChanged, signInWithPopup, signOut, type User } from 'firebase/auth';

import { auth } from 'utils/firebaseConfig';
import { appEnv } from 'config/env';
import {
  getUserAccessRecord,
  requestUserAccess,
  type UserAccessStatus,
} from 'apis/repositories/userAccessRepository';

export interface AuthContextValue {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  isAuthorized: boolean;
  canAccessApp: boolean;
  accessStatus: UserAccessStatus | 'not-requested';
  isAdmin: boolean;
  authError: string;
  loginWithGoogle: () => Promise<unknown>;
  requestAccess: () => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);
const provider = new GoogleAuthProvider();

const getIsAdmin = (user: User | null): boolean => {
  if (!user?.email) {
    return false;
  }

  return appEnv.admin.allowedEmails.includes(user.email.toLowerCase());
};

const getPendingAccessError = (
  hasUser: boolean,
  canAccessApp: boolean,
  accessStatus: UserAccessStatus | 'not-requested',
  authError: string
): string => {
  if (authError) {
    return authError;
  }

  if (!hasUser || canAccessApp) {
    return '';
  }

  if (accessStatus === 'rejected') {
    return 'Your access request was rejected by admin. You can request again.';
  }

  if (accessStatus === 'pending') {
    return 'Your access request is pending admin approval.';
  }

  return 'You need admin approval to use this app.';
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [authError, setAuthError] = useState('');
  const [accessStatus, setAccessStatus] = useState<UserAccessStatus | 'not-requested'>('not-requested');

  const refreshUserAccessStatus = async (nextUser: User | null): Promise<void> => {
    if (!nextUser) {
      setAccessStatus('not-requested');
      return;
    }

    if (getIsAdmin(nextUser)) {
      setAccessStatus('approved');
      return;
    }

    try {
      const record = await getUserAccessRecord(nextUser.uid);
      if (!record) {
        setAccessStatus('not-requested');
        return;
      }

      setAccessStatus(record.status || 'pending');
    } catch (error) {
      console.error('Unable to check user access status', error);
      setAccessStatus('not-requested');
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(
      auth,
      (nextUser) => {
        setAuthError('');
        setUser(nextUser);
        refreshUserAccessStatus(nextUser).finally(() => {
          setIsLoading(false);
        });
      },
      () => {
        setUser(null);
        setAccessStatus('not-requested');
        setIsLoading(false);
      }
    );

    return unsubscribe;
  }, []);

  const value = useMemo<AuthContextValue>(() => {
    const isAdmin = getIsAdmin(user);
    const hasUser = Boolean(user);
    const isAllowedByStaticList = Boolean(user?.email && appEnv.auth.allowedEmails.includes(user.email.toLowerCase()));
    const canAccessApp = isAdmin || isAllowedByStaticList || accessStatus === 'approved';

    return {
      user,
      isLoading,
      isAuthenticated: hasUser,
      isAuthorized: canAccessApp,
      canAccessApp,
      accessStatus,
      isAdmin,
      authError: getPendingAccessError(hasUser, canAccessApp, accessStatus, authError),
      loginWithGoogle: () => {
        setAuthError('');
        return signInWithPopup(auth, provider);
      },
      requestAccess: async () => {
        if (!user?.uid || !user.email) {
          throw new Error('Please login first to request access.');
        }

        await requestUserAccess({
          uid: user.uid,
          email: user.email,
        });
        await refreshUserAccessStatus(user);
      },
      logout: () => signOut(auth),
    };
  }, [user, isLoading, authError, accessStatus]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuthContext = (): AuthContextValue => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuthContext must be used inside AuthProvider');
  }

  return context;
};
